import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '../ui/input';
import { SimpleTable } from './K2Tables';
import { NesLocationTiles } from './NesLocationTiles';
import { fmtQty } from './itemsUtils';
import { K2DocDetail } from './K2DocDetail';
import { fmtCz, fmtKc } from './k2utils';

const DOC_COLUMNS = [
  { key: 'date', label: 'Datum', render: (r) => fmtCz(r.date), className: 'whitespace-nowrap' },
  { key: 'doc', label: 'Doklad', className: 'font-medium whitespace-nowrap' },
  { key: 'book', label: 'Řada', render: (r) => <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.book === '290' ? 'bg-sky-100 text-sky-800' : 'bg-violet-100 text-violet-800'}`}>{r.book}</span> },
  { key: 'warehouse', label: 'Sklad', render: (r) => <span title={r.warehouse_name}>{r.warehouse}</span> },
  { key: 'description', label: 'Popis', className: 'text-[#57534E]' },
  { key: 'item_count', label: 'Položek', num: true },
  { key: 'amount', label: 'Hodnota', num: true, render: (r) => fmtKc(r.amount), className: 'font-semibold' },
];

const NES_COLUMNS = [
  { key: 'article_code', label: 'Kód', className: 'whitespace-nowrap' },
  { key: 'article_name', label: 'Zboží', className: 'font-medium' },
  { key: 'batch', label: 'Šarže', className: 'whitespace-nowrap' },
  { key: 'batch_date', label: 'Datum šarže', render: (r) => fmtCz(r.batch_date), className: 'whitespace-nowrap' },
  { key: 'location', label: 'Umístění' },
  { key: 'quantity', label: 'Množství', num: true, render: (r) => fmtQty(r.quantity, r.unit) },
  { key: 'value', label: 'Hodnota', num: true, render: (r) => fmtKc(r.value), className: 'font-semibold text-[#DC2626]' },
];

const matches = (r, q, keys) => !q || keys.some((k) => String(r[k] || '').toLowerCase().includes(q));

const Section = ({ title, subtitle, q, setQ, testId, children }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-sm space-y-3" data-testid={testId}>
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-base md:text-lg font-bold text-[#052E16] font-['Manrope']">{title}</h2>
        <p className="text-xs text-[#57534E]">{subtitle}</p>
      </div>
      <div className="relative w-64">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat…" className="pl-9" data-testid={`${testId}-search`} />
      </div>
    </div>
    {children}
  </div>
);

export const K2DocsSection = ({ docs, items, itemsLoading }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const rows = useMemo(() => docs.filter((r) => matches(r, q.toLowerCase(), ['doc', 'description', 'warehouse', 'partner'])), [docs, q]);
  return (
    <Section title="Doklady výdejek (290 / 291)" subtitle={`${rows.length} dokladů ve vybraném období · kliknutím zobrazíte položky`} q={q} setQ={setQ} testId="k2-docs-section">
      <SimpleTable key={q + docs.length} testId="k2-docs-table" columns={DOC_COLUMNS} rows={rows} rowKey={(r) => r.rid} onRowClick={setOpen} />
      <K2DocDetail doc={open} items={items} loading={itemsLoading} onClose={() => setOpen(null)} />
    </Section>
  );
};

export const K2NesSection = ({ items }) => {
  const [q, setQ] = useState('');
  const [loc, setLoc] = useState(null);
  const rows = useMemo(() => items
    .filter((r) => !loc || (r.location || 'Bez umístění') === loc)
    .filter((r) => matches(r, q.toLowerCase(), ['article_code', 'article_name', 'batch', 'location']))
    .sort((a, b) => b.value - a.value), [items, q, loc]);
  return (
    <Section title="Sklad NES – aktuální stav" subtitle={`${rows.length} položek · celkem ${fmtKc(rows.reduce((a, r) => a + r.value, 0))}${loc ? ` · umístění ${loc}` : ''}`} q={q} setQ={setQ} testId="k2-nes-section">
      <NesLocationTiles items={items} active={loc} onSelect={setLoc} />
      {loc && <button type="button" onClick={() => setLoc(null)} className="text-xs text-[#16A34A] hover:underline" data-testid="k2-nes-loc-clear">Zobrazit všechna umístění</button>}
      <SimpleTable key={q + loc} testId="k2-nes-table" columns={NES_COLUMNS} rows={rows} rowKey={(r) => `${r.article_code}-${r.batch}-${r.location}`} />
    </Section>
  );
};
